import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import {
  Button,
  Container,
  Divider,
  Grid,
  Header,
  Icon,
  Message,
  Table,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import PageHeader from "../../app/layout/PageHeader";
import { useStore } from "../../app/stores/store";
import LoadingComponent from "../../app/layout/LoadingComponent";
import MyPaging from "../../app/common/form/MyPaging";
import { PagingParams } from "../../app/models/pagination";
import { format } from "date-fns";
import { formatCurrency } from "../../app/common/util/functions";
import ReactToPrint from "react-to-print";

export default observer(function PurchaseHistory() {
  const { t } = useTranslation(["common", "inventory"]);
  const componentRef = useRef<any>();
  const { id } = useParams<{ id: string }>();
  const { inventoryPartDetailsStore } = useStore();
  const {
    loadingInitial,
    loadPurchaseHistory,
    purchaseHistoryPagingParams,
    setPurchaseHistoryPagingParams,
    resetPurchaseHistoryRegistry,
    purchaseHistoryPagination,
    getSelectedPurchaseHistory,
    selectedPartDetails
  } = inventoryPartDetailsStore;

  useEffect(
    () => {
      if (id)
        loadPurchaseHistory(id);
    },
    [id, loadPurchaseHistory]
  );

  function listOnClick(value: number) {
    if (value > 0 && purchaseHistoryPagination && value < purchaseHistoryPagination?.totalPages + 1) {
      setPurchaseHistoryPagingParams(new PagingParams(value));
      resetPurchaseHistoryRegistry();
      loadPurchaseHistory(id!);
    }
  }
  if (loadingInitial)
    return (
      <LoadingComponent
        content={t("purchaseHistory_loading", { ns: "inventory" })}
      />
    );

  return (
    <>
      <Grid stackable columns={2}>
        <Grid.Column>
          <PageHeader
            header={`${t("purchaseHistory", { ns: "inventory" })}: ${selectedPartDetails?.partNumber ?? ""}`}
            type="h1"
            divider={false}
            getAlerts={true}
            addTitle={true}
          />
        </Grid.Column>
        <Grid.Column textAlign="right">
          <ReactToPrint
            trigger={() => (
              <Button color="green" type="button">
                <Icon name="print" />
                {t("estimate.print", { ns: "tickets" })}
              </Button>
            )}
            content={() => componentRef.current}
          />
          <Link to={`/inventoryIndex/partDetails/${id}`}>
            <Button
              className="save_button mobile_buttons"
              as={Link}
              to={`/inventoryIndex/partDetails/${id}`}
              icon="x"
              type="button"
              content={t("cancel", { ns: "common" })}
            />
          </Link>
        </Grid.Column>
      </Grid>
      <Divider />
      <Container className="page">
        <div ref={componentRef} className="printMargins">
        <Grid stackable columns={2} className="headerPrint">
          <Grid.Row>
            <Grid.Column>
              <PageHeader
                header={`${t("report", { ns: "reports" })}: ${t("purchaseHistory", {
                  ns: "inventory",
                })}- ${selectedPartDetails?.partNumber ?? ""}`}
                type="h2"
                divider={false}
                getAlerts={true}
                addTitle={true}
              />
            </Grid.Column>
            <Grid.Column textAlign="right">
              <Header
                content={`${format(new Date(), "PP")}`}
                as="h2"
               />
            </Grid.Column>
          </Grid.Row>
        </Grid>
          <Table striped unstackable className="dnxTable">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {t("labels.purchaseDate", { ns: "inventory" })}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t("labels.purchaseNumber", { ns: "inventory" })}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {`${t("labels.vendor", { ns: "inventory" })}`}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {`${t("labels.quantityPurchased", { ns: "inventory" })}`}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {`${t("labels.cost", { ns: "inventory" })}`}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {`${t("labels.totalCost", { ns: "inventory" })}`}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {getSelectedPurchaseHistory && getSelectedPurchaseHistory.length ? getSelectedPurchaseHistory.map(ph => (

                <Table.Row key={ph.id}>
                  <Table.Cell data-label={`${t("purchaseDate", { ns: "inventory" })}:`}>
                    {format(new Date(ph.purchaseDate), "MM/dd/yyyy")}
                  </Table.Cell>
                  <Table.Cell
                    data-label={`${t("purchaseNumber", {
                      ns: "inventory",
                    })}:`}
                  >
                    {ph.poNumber}
                  </Table.Cell>
                  <Table.Cell
                    data-label={`${t("vendor", {
                      ns: "inventory",
                    })}:`}
                  >
                    {ph.vendor}
                  </Table.Cell>
                  <Table.Cell
                    data-label={`${t("quantityPurchased", {
                      ns: "inventory",
                    })}:`}
                  >
                    {ph.quantityPurchased}
                  </Table.Cell>
                  <Table.Cell
                    data-label={`${t("cost", {
                      ns: "inventory",
                    })}:`}
                  >
                    {formatCurrency(ph.cost)}
                  </Table.Cell>
                  <Table.Cell
                    data-label={`${t("totalCost", {
                      ns: "inventory",
                    })}:`}
                  >
                    {formatCurrency(ph.totalCost)}
                  </Table.Cell>
                </Table.Row>
              )) : (
                <Table.Row key={"noItems1"}>
                  <Table.Cell
                    className="noPaddingLeft"
                    width="8"
                    colSpan={8}
                  >
                    <Message
                      color="yellow"
                      className="textAlignCenter fontStyleItalic"
                    >
                      {t("grid_noItems", { ns: "common" })}
                    </Message>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.Cell colSpan="6">
                  <MyPaging
                    itemCount={purchaseHistoryPagination ? purchaseHistoryPagination.totalItems : 10}
                    pageSize={purchaseHistoryPagingParams.pageSize}
                    currentPage={purchaseHistoryPagination ? purchaseHistoryPagination.currentPage : 1}
                    onClick={listOnClick}
                    onChange={listOnClick}
                    floated="right"
                  ></MyPaging>
                </Table.Cell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </div>

      </Container>
    </>
  );
});
